import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  useLocation,
  useNavigate,
} from 'react-router-dom'

import {
  getDailyDrop,
} from '../../api/drops'

import {
  GameDialog,
  type GameDialogAction,
  type GameDialogLine,
} from '../dialog/GameDialog'

import type {
  AuthStatus,
} from '../../hooks/useHeader'

import type {
  MeResponse,
} from '../../types/me'


interface DailyDropNoticeProps {
  status: AuthStatus
  me: MeResponse | null
}


type NoticeMode =
  | 'welcome'
  | 'daily'
  | null


const LOGIN_FLAG =
  'kraken:just-logged-in'


/*
 * Пока используем существующую
 * иллюстрацию Nova с главной.
 *
 * Позже достаточно поменять только
 * эту строку на отдельный портрет
 * для диалогов.
 */
const NOVA_AVATAR =
  'https://jjrr9e9l0o.cdn.twcstorage.ru/mascots/nova/default.webp'


function getTodayKey(): string {
  const now =
    new Date()

  const year =
    now.getFullYear()

  const month =
    String(
      now.getMonth() + 1,
    ).padStart(
      2,
      '0',
    )

  const day =
    String(
      now.getDate(),
    ).padStart(
      2,
      '0',
    )

  return `${year}-${month}-${day}`
}


function getDailyStorageKey(
  userId: number,
): string {
  return (
    `kraken:daily-drop-notice:`
    + `${userId}:${getTodayKey()}`
  )
}


function getSpinWord(
  count: number,
): string {
  const mod10 =
    count % 10

  const mod100 =
    count % 100

  if (
    mod10 === 1 &&
    mod100 !== 11
  ) {
    return 'бесплатная прокрутка'
  }

  if (
    mod10 >= 2 &&
    mod10 <= 4 &&
    (
      mod100 < 12 ||
      mod100 > 14
    )
  ) {
    return 'бесплатные прокрутки'
  }

  return 'бесплатных прокруток'
}


export function DailyDropNotice({
  status,
  me,
}: DailyDropNoticeProps) {
  const navigate =
    useNavigate()

  const location =
    useLocation()

  const checkedUserRef =
    useRef<number | null>(
      null,
    )

  const [
    mode,
    setMode,
  ] = useState<NoticeMode>(
    null,
  )

  const [
    freeRemaining,
    setFreeRemaining,
  ] = useState(0)

  const [
    streak,
    setStreak,
  ] = useState(0)


  const closeNotice =
    useCallback(() => {
      if (!me) {
        setMode(null)

        return
      }

      /*
       * Daily-напоминание после закрытия
       * больше сегодня не показываем.
       *
       * Если welcome уже знает про
       * доступный Daily Drop, закрытие
       * также считается отказом от
       * предложения на сегодня.
       */
      if (
        mode === 'daily' ||
        (
          mode === 'welcome' &&
          freeRemaining > 0
        )
      ) {
        localStorage.setItem(
          getDailyStorageKey(
            me.id,
          ),
          'dismissed',
        )
      }

      /*
       * Welcome после настоящего login
       * показывается только один раз.
       */
      sessionStorage.removeItem(
        LOGIN_FLAG,
      )

      setMode(null)
    }, [
      me,
      mode,
      freeRemaining,
    ])


  const claimBonus =
    useCallback(() => {
      if (!me) {
        return
      }

      /*
       * Пользователь принял предложение.
       * Сегодня глобальное напоминание
       * больше не показываем.
       */
      localStorage.setItem(
        getDailyStorageKey(
          me.id,
        ),
        'claimed',
      )

      sessionStorage.removeItem(
        LOGIN_FLAG,
      )

      setMode(null)

      navigate(
        '/daily/',
      )
    }, [
      me,
      navigate,
    ])


  useEffect(() => {
    if (
      status !== 'authenticated' ||
      !me
    ) {
      checkedUserRef.current =
        null

      setMode(null)

      return
    }

    /*
     * Для конкретного пользователя
     * проверяем состояние один раз.
     *
     * Это также защищает от повторного
     * окна из-за StrictMode.
     */
    if (
      checkedUserRef.current ===
        me.id
    ) {
      return
    }

    checkedUserRef.current =
      me.id

    const justLoggedIn =
      sessionStorage.getItem(
        LOGIN_FLAG,
      ) === '1'

    /*
     * После настоящего login welcome
     * показываем гарантированно.
     *
     * Daily Drop API для этого
     * не требуется.
     */
    if (justLoggedIn) {
      setFreeRemaining(0)
      setStreak(0)
      setMode('welcome')

      /*
       * Отдельно пытаемся обогатить
       * диалог информацией о Daily Drop.
       */
      void getDailyDrop()
        .then(
          (response) => {
            setFreeRemaining(
              response.spin
                .free_remaining,
            )

            setStreak(
              response.user.streak,
            )
          },
        )
        .catch(
          (error) => {
            console.error(
              'Failed to enrich welcome with Daily Drop:',
              error,
            )
          },
        )

      return
    }

    /*
     * На самой странице Daily
     * напоминание не показываем.
     */
    if (
      location.pathname ===
        '/daily' ||
      location.pathname ===
        '/daily/'
    ) {
      return
    }

    const storageKey =
      getDailyStorageKey(
        me.id,
      )

    /*
     * Сегодня пользователь уже
     * видел это предложение.
     */
    if (
      localStorage.getItem(
        storageKey,
      )
    ) {
      return
    }

    /*
     * В обычной сессии показываем
     * диалог только если действительно
     * есть бесплатная прокрутка.
     */
    void getDailyDrop()
      .then(
        (response) => {
          const remaining =
            response.spin
              .free_remaining

          if (
            remaining <= 0
          ) {
            return
          }

          setFreeRemaining(
            remaining,
          )

          setStreak(
            response.user.streak,
          )

          setMode(
            'daily',
          )
        },
      )
      .catch(
        (error) => {
          /*
           * Daily Drop — дополнительная
           * механика. Ошибка API не должна
           * ломать Академию.
           */
          console.error(
            'Failed to check Daily Drop:',
            error,
          )
        },
      )
  }, [
    status,
    me,
    location.pathname,
  ])


  const lines =
    useMemo<GameDialogLine[]>(
      () => {
        if (
          !mode ||
          !me
        ) {
          return []
        }

        const spinWord =
          getSpinWord(
            freeRemaining,
          )

        if (
          mode === 'welcome'
        ) {
          const welcomeLines:
            GameDialogLine[] = [
              {
                text:
                  `С возвращением, ${me.name}. Рада снова видеть тебя в Kraken Academy.`,
              },
            ]

          if (
            freeRemaining > 0
          ) {
            welcomeLines.push(
              {
                text:
                  'Кстати... Пока ты здесь, у меня для тебя кое-что есть.',
              },
              {
                text:
                  `Сегодня тебе доступно ${freeRemaining} ${spinWord}. Текущий стрик — ${streak}.`,
              },
              {
                text:
                  'Заглянем в Daily Drop?',
              },
            )

            return welcomeLines
          }

          welcomeLines.push(
            {
              text:
                'Продолжай обучение, закрывай стенды и двигайся дальше.',
            },
          )

          return welcomeLines
        }

        return [
          {
            text:
              `Эй, ${me.name}. Ты кое-что забыл...`,
          },
          {
            text:
              `Тебя всё ещё ждёт ${freeRemaining} ${spinWord}.`,
          },
          {
            text:
              `Твой текущий стрик — ${streak}. Заглянем в Daily Drop?`,
          },
        ]
      },
      [
        mode,
        me,
        freeRemaining,
        streak,
      ],
    )


  const actions =
    useMemo<GameDialogAction[]>(
      () => {
        if (
          !mode ||
          !me
        ) {
          return []
        }

        if (
          freeRemaining > 0
        ) {
          return [
            {
              label:
                'Забрать награду',
              primary: true,
              onClick:
                claimBonus,
            },
            {
              label:
                'Не сейчас',
              onClick:
                closeNotice,
            },
          ]
        }

        return [
          {
            label:
              'Продолжить',
            primary: true,
            onClick:
              closeNotice,
          },
        ]
      },
      [
        mode,
        me,
        freeRemaining,
        claimBonus,
        closeNotice,
      ],
    )


  if (
    !mode ||
    !me
  ) {
    return null
  }


  return (
    <GameDialog
      open
      avatar={NOVA_AVATAR}
      speaker="NOVA"
      lines={lines}
      actions={actions}
      typingSpeed={22}
      onClose={closeNotice}
    />
  )
}
